import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Building2,
  FileText,
  IdCard,
  Image as ImageIcon,
  MapPin,
  Phone,
  Upload,
} from 'lucide-react';
import { Drawer } from '@/components/common/Drawer';
import { AccordionGroup, AccordionSection } from '@/components/common/Accordion';
import { Button } from '@/components/common/Button';
import { FormField } from '@/components/forms/FormField';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { LogoAvatar } from '@/components/common/LogoAvatar';
import {
  createEmpresa,
  updateEmpresa,
  uploadLogoEmpresa,
  type Empresa,
  type EmpresaInsert,
} from '@/services/api/empresas';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (empresa: Empresa) => void;
  /** Si null → modo "nueva empresa". */
  editing: Empresa | null;
}

type FormState = Omit<EmpresaInsert, 'cuit' | 'nombre' | 'razon_social'> & {
  cuit: string;
  nombre: string;
  razon_social: string;
};

function emptyForm(): FormState {
  return {
    nombre: '',
    razon_social: '',
    cuit: '',
    condicion_iva: 'responsable_inscripto',
    domicilio_fiscal: '',
    localidad: '',
    provincia: '',
    codigo_postal: '',
    email_contacto: '',
    email_remitente_nombre: '',
    email_reply_to: '',
    telefono: '',
    logo_url: null,
    activo: true,
    arca_intervalo_emision_seg: 15,
  };
}

export function EmpresaEditDialog({ open, onClose, onSaved, editing }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        nombre: editing.nombre,
        razon_social: editing.razon_social,
        cuit: editing.cuit,
        condicion_iva: editing.condicion_iva,
        domicilio_fiscal: editing.domicilio_fiscal ?? '',
        localidad: editing.localidad ?? '',
        provincia: editing.provincia ?? '',
        codigo_postal: editing.codigo_postal ?? '',
        email_contacto: editing.email_contacto ?? '',
        email_remitente_nombre: editing.email_remitente_nombre ?? '',
        email_reply_to: editing.email_reply_to ?? '',
        telefono: editing.telefono ?? '',
        logo_url: editing.logo_url,
        activo: editing.activo,
        arca_intervalo_emision_seg: editing.arca_intervalo_emision_seg ?? 15,
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, editing]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onUploadLogo(file: File) {
    if (!editing) {
      toast.error('Primero guardá la empresa y después subís el logo.');
      return;
    }
    setUploading(true);
    const res = await uploadLogoEmpresa(editing.id, file);
    setUploading(false);
    if (!res.ok) {
      toast.error(res.error.message);
      return;
    }
    // Persistimos el logo_url en la tabla
    const upd = await updateEmpresa(editing.id, { logo_url: res.data });
    if (!upd.ok) {
      toast.error(upd.error.message);
      return;
    }
    set('logo_url', res.data);
    toast.success('Logo actualizado. ✨');
    onSaved(upd.data);
  }

  async function onSubmit() {
    if (!form.nombre.trim() || !form.razon_social.trim()) {
      toast.error('Nombre y razón social son obligatorios.');
      return;
    }
    const cuit = form.cuit.replace(/\D/g, '');
    if (!/^\d{11}$/.test(cuit)) {
      toast.error('El CUIT debe tener 11 dígitos.');
      return;
    }
    setSubmitting(true);
    const payload: FormState = {
      ...form,
      cuit,
      nombre: form.nombre.trim(),
      razon_social: form.razon_social.trim(),
    };
    const res = editing
      ? await updateEmpresa(editing.id, payload)
      : await createEmpresa(payload as EmpresaInsert);
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.error.message);
      return;
    }
    toast.success(editing ? 'Empresa actualizada. ✨' : '¡Empresa creada!');
    onSaved(res.data);
    onClose();
  }

  const sumFiscal = [form.cuit, form.condicion_iva?.replace('_', ' ')]
    .filter(Boolean)
    .join(' · ');
  const sumDir = [form.domicilio_fiscal, form.localidad].filter(Boolean).join(', ');
  const sumContacto = [form.email_contacto, form.telefono].filter(Boolean).join(' · ');

  return (
    <Drawer
      open={open}
      onClose={submitting || uploading ? () => {} : onClose}
      width={560}
      kicker={editing ? 'Editar empresa' : 'Nueva empresa'}
      title={editing ? editing.nombre : 'Alta'}
      description="Datos fiscales + logo. Los cambios impactan en todas las facturas emitidas desde acá."
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting || uploading}
          >
            Cancelar
          </Button>
          <Button onClick={onSubmit} loading={submitting}>
            {editing ? 'Guardar cambios' : 'Crear empresa'}
          </Button>
        </>
      }
    >
      {/* Identidad visual: logo + nombres esenciales */}
      <div className="mb-3 flex items-center gap-4 rounded-md border border-blue-30 bg-blue-10/30 p-4">
        <LogoAvatar
          src={form.logo_url}
          name={form.nombre || 'Empresa'}
          size={64}
          shape="square"
        />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
              Nombre *
            </label>
            <Input
              value={form.nombre}
              onChange={(e) => set('nombre', e.target.value)}
              placeholder="Administración Global"
              className="mt-0.5 h-9"
            />
          </div>
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadLogo(f);
                }}
              />
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<Upload size={13} />}
                onClick={() => fileRef.current?.click()}
                loading={uploading}
              >
                {form.logo_url ? 'Cambiar logo' : 'Subir logo'}
              </Button>
              {form.logo_url && (
                <span className="text-[11px] text-neutral-muted">
                  PNG/JPG/WEBP · se reemplaza el actual
                </span>
              )}
            </div>
          ) : (
            <p className="flex items-center gap-1.5 text-[11px] text-neutral-muted">
              <ImageIcon size={11} /> El logo se carga después de crear la empresa.
            </p>
          )}
        </div>
      </div>

      <AccordionGroup initialOpen="fiscal">
        <AccordionSection
          id="fiscal"
          icon={<IdCard size={15} />}
          title="Datos fiscales"
          summary={sumFiscal}
        >
          <div className="grid grid-cols-1 gap-3">
            <FormField label="Razón social" required hint="La que va en facturas ARCA">
              <Input
                value={form.razon_social}
                onChange={(e) => set('razon_social', e.target.value)}
                placeholder="Global Servicios Integrados SRL"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="CUIT" required hint="11 dígitos, sin guiones">
                <Input
                  value={form.cuit}
                  onChange={(e) => set('cuit', e.target.value.replace(/\D/g, ''))}
                  placeholder="30XXXXXXXXX"
                  maxLength={11}
                />
              </FormField>
              <FormField label="Condición IVA" required>
                <Select
                  value={form.condicion_iva ?? 'responsable_inscripto'}
                  onChange={(e) => set('condicion_iva', e.target.value)}
                >
                  <option value="responsable_inscripto">Responsable inscripto</option>
                  <option value="monotributo">Monotributo</option>
                  <option value="exento">Exento</option>
                </Select>
              </FormField>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection
          id="direccion"
          icon={<MapPin size={15} />}
          title="Domicilio fiscal"
          summary={sumDir}
        >
          <div className="grid grid-cols-1 gap-3">
            <FormField label="Dirección">
              <Input
                value={form.domicilio_fiscal ?? ''}
                onChange={(e) => set('domicilio_fiscal', e.target.value)}
              />
            </FormField>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Localidad" className="col-span-2">
                <Input
                  value={form.localidad ?? ''}
                  onChange={(e) => set('localidad', e.target.value)}
                />
              </FormField>
              <FormField label="CP">
                <Input
                  value={form.codigo_postal ?? ''}
                  onChange={(e) => set('codigo_postal', e.target.value)}
                />
              </FormField>
            </div>
            <FormField label="Provincia">
              <Input
                value={form.provincia ?? ''}
                onChange={(e) => set('provincia', e.target.value)}
              />
            </FormField>
          </div>
        </AccordionSection>

        <AccordionSection
          id="contacto"
          icon={<Phone size={15} />}
          title="Contacto"
          summary={sumContacto}
        >
          <div className="grid grid-cols-1 gap-3">
            <FormField
              label="Email de contacto"
              hint="General — aparece en reportes y es el fallback del reply-to"
            >
              <Input
                type="email"
                value={form.email_contacto ?? ''}
                onChange={(e) => set('email_contacto', e.target.value)}
                placeholder="contacto@tuempresa.com"
              />
            </FormField>
            <FormField
              label="Nombre que aparece como remitente"
              hint="Lo ven los administradores en su bandeja como “Nombre <facturacion@manaxer.com.ar>”. Si lo dejás vacío, sale como “MANAXER”."
            >
              <Input
                value={form.email_remitente_nombre ?? ''}
                onChange={(e) => set('email_remitente_nombre', e.target.value)}
                placeholder={form.nombre || 'Administración Global'}
              />
            </FormField>
            <FormField
              label="Reply-to de facturación"
              hint="Todos los mails salen desde facturacion@manaxer.com.ar con reply-to a esta casilla. Si lo dejás vacío, se usa el email de contacto."
            >
              <Input
                type="email"
                value={form.email_reply_to ?? ''}
                onChange={(e) => set('email_reply_to', e.target.value)}
                placeholder="cobranzas@tuempresa.com"
              />
            </FormField>
            <FormField label="Teléfono">
              <Input
                value={form.telefono ?? ''}
                onChange={(e) => set('telefono', e.target.value)}
              />
            </FormField>
            <FormField
              label="Intervalo entre emisiones ARCA (segundos)"
              hint="Cuánto espera el lote entre cada solicitud a AFIP. 15s es el default seguro. Bajá si AFIP responde rápido y subí si tenés rechazos transitorios. Mín 5, máx 120."
            >
              <Input
                type="number"
                min={5}
                max={120}
                value={String(form.arca_intervalo_emision_seg ?? 15)}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  set(
                    'arca_intervalo_emision_seg',
                    Math.max(5, Math.min(120, isNaN(n) ? 15 : n)),
                  );
                }}
              />
            </FormField>
          </div>
        </AccordionSection>

        {editing && (
          <AccordionSection
            id="tecnica"
            icon={<FileText size={15} />}
            title="Información técnica"
            summary={`ID ${editing.id.slice(0, 8)}…`}
          >
            <dl className="space-y-1 text-xs text-neutral-muted">
              <Row k="ID interno" v={<code className="font-mono">{editing.id}</code>} />
              <Row
                k="Próximo DNI ficticio"
                v={
                  <code className="font-mono">
                    {editing.proximo_dni_ficticio ?? '99000001'}
                  </code>
                }
              />
              <Row k="Creado" v={new Date(editing.created_at).toLocaleString('es-AR')} />
              <Row
                k="Actualizado"
                v={new Date(editing.updated_at).toLocaleString('es-AR')}
              />
              <Row
                k="Logo URL"
                v={
                  editing.logo_url ? (
                    <a
                      href={editing.logo_url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-blue hover:underline"
                    >
                      abrir ↗
                    </a>
                  ) : (
                    '— no cargado —'
                  )
                }
              />
            </dl>
          </AccordionSection>
        )}
      </AccordionGroup>

      {/* Marcador visual al pie — sin necesidad de ícono */}
      <div className="mt-4 flex items-center gap-2 text-xs text-neutral-muted">
        <Building2 size={12} />
        <span>
          Esta ficha impacta en todas las facturas que emite{' '}
          <b className="text-neutral-ink">{form.nombre || 'la empresa'}</b> hacia sus
          clientes.
        </span>
      </div>
    </Drawer>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt>{k}</dt>
      <dd className="text-neutral-ink">{v}</dd>
    </div>
  );
}
